import "react-dates/initialize"
import angular from "angular"
import React from "react"
import { react2angular } from "react2angular"
import moment from "moment"

import Modal from "@paprika/modal"
import Spinner from "@paprika/spinner"
import FormElement, { Fieldset } from "@paprika/form-element"
import ListBox from "@paprika/list-box"
import Button from "@paprika/button"
import Select from "@paprika/select"
import { useMediaQuery } from "react-responsive"

import { getDayDetailsByUnitAndStartDate } from "../../utils/anytime"
import { DateRangePicker } from "react-dates"
import "react-dates/lib/css/_datepicker.css"

import "./availabilityCheckModal.scss"

const { Label, Content, Instructions, Layout, Error } = FormElement
const { LeftCol, RightCol } = Layout

const presentableDateFormat = "MMMM Do"

const campsiteOccupantTypeIds = {
  polmanter: { adults: { max: 6 }, children: { max: 5 }, infants: { max: 5 } },
}

const noResultsMessageByCampsite = {
  tregarton: `<p>Unfortunately we are fully booked for your chosen dates, however, to view our short breaks/cancellation availability list please visit http://www.tregarton.co.uk/late-availability/. </p><p>Email: pitch@tregarton.co.uk</p><p>Telephone: 01726 843666</p>`,
  polmanter: `<p>If you are still unable to find what you're looking for, don't hesitate to call us on 01736 795640 or email us at <a href="mailto:reception@polmanter.com" target="_blank">reception@polmanter.com</a> to discuss what is available.</p><p>Please note advance bookings require a minimum stay of 3 nights, apart from during peak season (Friday 19th July - Thursday 29th August 2024) when it’s 7 nights. In peak season arrival/departure days are Friday, Saturday and Sunday.</p>`,
  gateway: `<p>No results. Please change your search criteria.</p><p>Please check the Touring & Camping Tariffs page for details of minimum stay requirements.</p>`,
  trevornick: `<p>Sorry, we haven't got what you're looking for, please try amending your arrival date and search again.</p><p>Alternatively, you can call us on 01637 830531 to check availability and make a booking.</p>`,
  blaircastle: `<p>Sorry no results were found that match your search. Please broaden your search criteria or try to flex your dates.</p><p>Alternatively please call reception on 01796 481263 to check availability.</p>`,
  "4seasons": `<p>Sorry no results were found that match your search. Please broaden your search criteria or try to flex your dates.</p><p>Alternatively please call reception on +44 (0)1686 430654 to check availability.</p>`,
  Deerpark: `<p>Sorry no results were found that match your search. Please broaden your search criteria or try to flex your dates.</p><p>Alternatively please call the bookings team on +44 (0)7983 294 663 to check availability.</p>`,
  Deerpark2: `<p>Sorry no results were found that match your search. Please broaden your search criteria or try to flex your dates.</p><p>Alternatively please call the bookings team on +44 (0)7983 294 663 to check availability.</p>`,
  orchardview: `<p>Sorry no results were found that match your search. Please try different dates or pitch type.</p><p>Alternatively please call the bookings team on +44 (0)1844 396 301 to check availability.</p>`,
}

const holidayTypeCategoryMapping = {
  polmanter: {
    1: "Self catering",
    2: "Touring & Camping",
    3: "Glamping",
  },
  blaircastle: {
    1: "Caravans, Lodges & Pods",
    2: "Touring & Camping",
  },
  trevornick: {
    1: "Caravans, Ready Tents, Lodges & Cottages",
    2: "Touring & Camping",
  },
  gateway: {
    showCategorySelect: false,
    useDefaultCategoryId: 2,
    1: "Caravans, Lodges & Pods",
    2: "Touring & Camping",
  },
  "4seasons": {
    showCategorySelect: false,
    useDefaultCategoryId: 2,
    1: "unsure",
    2: "unsure 2",
  },
  Deerpark: {
    showCategorySelect: false,
    useDefaultCategoryId: 2,
    1: "unsure",
    2: "unsure 2",
  },
  Deerpark2: {
    showCategorySelect: false,
    useDefaultCategoryId: 2,
    1: "unsure",
    2: "unsure 2",
  },
  tregarton: {
    showCategorySelect: false,
  },
  orchardview: {
    useDefaultCategoryId: 1,
    showCategorySelect: true,
    showUnitSelect: false,
  },
}

function renderSummary(numOfNights) {
  if (numOfNights) {
    return (
      <div className="num-of-nights">
        <span style={{ marginRight: "10px" }}>Duration: </span>
        {numOfNights} night{Number(numOfNights) > 1 && "s"}
      </div>
    )
  }
}

function isDayBlocked(momentDate, gemaParkAvailabilityDates, initialDateSelected) {
  if (initialDateSelected) return false
  if (gemaParkAvailabilityDates.includes(momentDate.format("YYYYMMDD"))) return false
  return true
}

function getNumOfNights(startDate, endDate) {
  return startDate && endDate ? endDate.diff(startDate, "days") : null
}

function getAccomodationId(unitTypes) {
  if (unitTypes && unitTypes.length === 1) {
    return unitTypes[0].accommodationTypeID
  }
  return null
}

function AvailabilityCheckModal(props) {
  const [startDate, setStartDate] = React.useState(props.initData?.startDate || null)
  const [endDate, setEndDate] = React.useState(props.initData?.endDate || null)
  const [focusedInput, setFocusedInput] = React.useState(null)
  const [unitId, setUnitId] = React.useState(props.initData?.unitId || getAccomodationId(props.unitTypes))
  const [bookableDurationData, setBookableDurationData] = React.useState(null)
  const [endDateError, setEndDateError] = React.useState(null)
  const [numOfNights, setNumOfNights] = React.useState(getNumOfNights(startDate, endDate))
  const [categoryId, setCategoryId] = React.useState(
    props.initData?.categoryId || holidayTypeCategoryMapping[props.campsiteinfo.name]?.useDefaultCategoryId || null
  )
  // stayTypeId is in state (not derived) so it carries a unique-per-row identifier
  // through the URL. GemaPark's GetStayTypes can return multiple rows sharing a
  // categoryID (e.g. {1: Self Catering, 1: Glamping, 2: Touring & Camping}), so
  // categoryId alone is ambiguous — stayTypeId disambiguates which row the user
  // actually picked. initData.stayTypeId is populated from a URL param so pasteable
  // links land on the right row.
  //
  // The useEffect below handles the legacy Modify-reopen path: URLs without a
  // staytypeid segment still pass categoryId, and we derive stayTypeId from the
  // first categoryData match once data loads. The useEffect only fires when
  // stayTypeId is unset, so a user click (which sets it directly) is never
  // overridden.
  const [stayTypeId, setStayTypeId] = React.useState(props.initData?.stayTypeId ?? null)
  React.useEffect(() => {
    if (stayTypeId != null) return
    if (categoryId == null || !props.categoryData) return
    const match = props.categoryData.find((item) => item.categoryID === categoryId)
    if (match) setStayTypeId(match.stayTypeID)
  }, [stayTypeId, categoryId, props.categoryData])
  const [numOfAdults, setNumOfAdults] = React.useState(props.initData?.numOfAdults || 2)
  const [numOfChildren, setNumOfChildren] = React.useState(props.initData?.numOfChildren || 0)
  const [numOfInfants, setNumOfInfants] = React.useState(props.initData?.numOfInfants || 0)

  const isMobile = useMediaQuery({ query: "(max-width: 1023px)" })
  const [gemaParkAvailabilityDates, setGemaParkAvailabilityDates] = React.useState(null)

  const [gemaParkMinimumNights, setGemaParkMinimumNights] = React.useState(null)
  const [anytimeSelectedDateInfo, setAnytimeSelectedDateInfo] = React.useState(null)
  const [initialDateSelected, setInitialDateSelected] = React.useState(false)
  const isGemaParkBookingSystem = props.campsiteinfo.booksystemname === "GemaPark"
  const isAnytimeBookingSystem = props.campsiteinfo.booksystemname === "Anytime"

  const isCategoryRequired = holidayTypeCategoryMapping[props.campsiteinfo.name]?.showCategorySelect !== false
  const isUnitRequired = holidayTypeCategoryMapping[props.campsiteinfo.name]?.showUnitSelect !== false
  const [isModifyingDate, setIsModifyingDate] = React.useState(true)
  const isUnitSelectorShowing = isAnytimeBookingSystem
    ? true
    : isGemaParkBookingSystem && props.unitTypes
      ? categoryId === 2 && props.unitTypes.length > 1
      : false

  const isUnitIdValid = isUnitRequired && isUnitSelectorShowing ? Boolean(unitId) : true
  const isCategoryIdValid = isCategoryRequired ? Boolean(categoryId) : true

  const isApplyEnabled =
    startDate &&
    endDate &&
    isUnitIdValid &&
    !props.isLoading &&
    isCategoryIdValid &&
    !endDateError &&
    (props.campsiteinfo.showOccupancyChoices ? props.minMaxOccupantTypesForUnitsData : true)
  const formElementStyle = { marginBottom: "20px" }

  function handleUnitIdChange(index) {
    const unitId = props.unitTypes[index].accommodationTypeID
    setUnitId(unitId)
  }

  function getBookableDatesForCategory({ categoryId, stayTypeId }) {
    fetch(
      `/api/availability/GetBookableDates/${props.campsiteinfo.name}/${props.campsiteinfo.gemaparkparkid}/${categoryId}/${stayTypeId}`
    )
      .then((response) => response.json())
      .then((response) => {
        setGemaParkAvailabilityDates(response.data)
      })
  }

  if (isGemaParkBookingSystem) {
    let mounted = true
    React.useEffect(() => {
      if (mounted) {
        if (categoryId && stayTypeId) {
          getBookableDatesForCategory({ categoryId, stayTypeId })
        }
        if (startDate) {
          setGemaparkBookableDurations(startDate)
        }
      }
      return function cleanup() {
        mounted = false
      }
    }, [categoryId, stayTypeId, startDate])

    React.useEffect(() => {
      if (mounted && props.unitTypes && !unitId) {
        setUnitId(getAccomodationId(props.unitTypes))
      }
      return function cleanup() {
        mounted = false
      }
    }, [props.unitTypes])
  }

  function handleCategoryIdChange(index) {
    const selectedOption = props.categoryData[index]
    setCategoryId(selectedOption.categoryID)
    setStayTypeId(selectedOption.stayTypeID)
    setStartDate(null)
    setEndDate(null)
    setEndDateError(null)
    // Reset unitId to null when changing category - different categories have different accommodation types
    setUnitId(null)
    if (isGemaParkBookingSystem) {
      getBookableDatesForCategory({ categoryId: selectedOption.categoryID, stayTypeId: selectedOption.stayTypeID })
    }
  }

  const renderInDiv = () => (
    <div className="availability-check-mobile">
      <div className="availability-check-mobile-header">
        <Button
          kind={Button.types.kind.MINOR}
          className="availability-check-mobile-close-button"
          onClick={props.onCloseModal}
        >
          X
        </Button>
      </div>
      {renderContent()}
    </div>
  )

  const renderInModal = () => {
    return (
      <Modal
        isOpen
        onClose={() => props.onCloseModal()}
        size="medium"
        className="availability-check-modal"
        style={{ overflow: "visible" }}
      >
        <Modal.Header>Availability check</Modal.Header>
        <Modal.Content style={{ minHeight: "200px", borderTop: 0, boxShadow: "none", borderRadius: 0 }}>
          {renderContent()}
        </Modal.Content>
      </Modal>
    )
  }

  const checkEndDateIsValid = (endDate) => {
    if (bookableDurationData) {
      if (endDate.isAfter(moment(bookableDurationData[bookableDurationData.length - 1].date, "YYYYMMDD"))) {
        setEndDateError(null)
        return
      }

      const endDateString = endDate.format("YYYYMMDD")
      const endDateIndex = bookableDurationData.findIndex((item) => item.date === endDateString)
      const endDateValid = bookableDurationData[endDateIndex - 1]?.valid
      if (!endDateValid) {
        const validEndDates = bookableDurationData.filter((item, index) => {
          const dateMinusOne = bookableDurationData[index - 1]
          if (dateMinusOne?.valid) {
            return true
          }
        })
        const validOptionsString = validEndDates.length
          ? validEndDates.map((item) => ` ${moment(item.date).format("MM/DD")}`)
          : ""
        const validEndDatesError = `End date invalid. Valid options are ${validOptionsString}`
        setEndDateError(validEndDatesError)
      } else {
        setEndDateError(null)
      }
    }
  }

  const setGemaparkBookableDurations = (startDate) => {
    // On a Modify-reopen URL without ?staytypeid, stayTypeId is null until the effect above resolves it
    // from categoryData. Fetching now would send the literal "null" (the server rejects it with 400);
    // the effect re-runs once stayTypeId is set.
    if (!categoryId || !stayTypeId) return
    fetch(
      `/api/availability/GetBookableDurations/${props.campsiteinfo.name}/${
        props.campsiteinfo.gemaparkparkid
      }/${categoryId}/${stayTypeId}/${startDate.format("YYYYMMDD")}`
    )
      .then((response) => response.json())
      .then((data) => {
        const firstValidEndDateIndex = data.data.findIndex((item) => item.valid)
        const minNights = data.data[firstValidEndDateIndex]?.nights
        setBookableDurationData(data.data)
        setGemaParkMinimumNights(minNights)
      })
  }

  const handleDatesChange = ({ startDate: changedStartDate, endDate: changedEndDate }) => {
    if (isGemaParkBookingSystem) {
      if (changedStartDate && changedStartDate.isSame(startDate) === false) {
        setBookableDurationData(null)
        setGemaParkMinimumNights(null)
        setEndDate(null)
        setStartDate(changedStartDate)
        setGemaparkBookableDurations(changedStartDate)
      }

      if (changedEndDate && changedEndDate.isSame(endDate) === false) {
        checkEndDateIsValid(changedEndDate)
        setEndDate(changedEndDate)
        setNumOfNights(getNumOfNights(startDate, changedEndDate))
        setIsModifyingDate(false)
      }
    }

    if (isAnytimeBookingSystem) {
      if (changedStartDate && changedStartDate.isSame(startDate) === false) {
        if (unitId) {
          const dateDetails = getDayDetailsByUnitAndStartDate(
            props.anytimeMinNightsAndPricingDataAllUnits,
            unitId,
            changedStartDate
          )
          setAnytimeSelectedDateInfo(dateDetails)
        }
      }
      if (changedEndDate && changedEndDate.isSame(endDate) === false) {
        setIsModifyingDate(false)
      }
      setNumOfNights(getNumOfNights(changedStartDate, changedEndDate))
      setStartDate(changedStartDate)
      setEndDate(changedEndDate)
    }
  }

  const handleDateInputFocusChange = (focusedInput) => {
    if (isGemaParkBookingSystem) {
      if (focusedInput === "endDate") {
        setInitialDateSelected(true)
      }
      // Only clear end date when user explicitly wants to change start date
      // Don't clear during calendar interactions when already selecting end date
      if (focusedInput === "startDate") {
        setInitialDateSelected(false)
        // Only clear endDate if focus is explicitly returning to start date field
        // and we're not in the middle of selecting an end date
        if (!initialDateSelected || focusedInput === null) {
          setEndDate(null)
        }
      }
      setFocusedInput(focusedInput)
    }

    if (isAnytimeBookingSystem) {
      if (focusedInput === "endDate") {
        setInitialDateSelected(true)
      }
      setFocusedInput(focusedInput)
    }
  }

  const dateRangeExtraProps = isGemaParkBookingSystem
    ? {
        disabled: !gemaParkAvailabilityDates,
        isDayBlocked: (momentDate) => isDayBlocked(momentDate, gemaParkAvailabilityDates, initialDateSelected),
        minimumNights: gemaParkMinimumNights,
      }
    : isAnytimeBookingSystem
      ? {
          disabled: !isUnitIdValid || !isCategoryIdValid,
          minimumNights: anytimeSelectedDateInfo?.minNights,
        }
      : {}

  const renderDateRangePicker = () => {
    if (!isModifyingDate) {
      return (
        <div style={{ fontSize: "18px", display: "flex" }}>
          <div style={{ flex: 1 }}>
            <p>
              {startDate.format(presentableDateFormat)} - {endDate.format(presentableDateFormat)}
            </p>
            <p>{renderSummary(numOfNights)}</p>
          </div>
          <Button className="modify-button" kind={Button.types.kind.PRIMARY} onClick={() => setIsModifyingDate(true)}>
            Modify
          </Button>
        </div>
      )
    }
    return (
      <DateRangePicker
        startDatePlaceholderText="Arrival date"
        endDatePlaceholderText="Departure date"
        startDate={startDate} // momentPropTypes.momentObj or null,
        startDateId="your_unique_start_date_id" // PropTypes.string.isRequired,
        endDate={endDate} // momentPropTypes.momentObj or null,
        endDateId="your_unique_end_date_id" // PropTypes.string.isRequired,
        onDatesChange={handleDatesChange} // PropTypes.func.isRequired,
        focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
        onFocusChange={handleDateInputFocusChange} // PropTypes.func.isRequired,
        orientation="horizontal"
        readOnly={true}
        displayFormat={() => "DD/MM/YYYY"}
        hideKeyboardShortcutsPanel={true}
        numberOfMonths={isMobile ? 1 : 2}
        {...dateRangeExtraProps}
      />
    )
  }

  const renderUnitTypeSelector = (a11yProps) =>
    props.unitTypes && props.unitTypes.length ? (
      <ListBox {...a11yProps} onChange={handleUnitIdChange}>
        <ListBox.Trigger hasClearButton={false} />
        {props.unitTypes.map((item) => (
          <ListBox.Option isSelected={item.accommodationTypeID === unitId} key={item.accommodationTypeID}>
            {item.name}
          </ListBox.Option>
        ))}
      </ListBox>
    ) : (
      <Spinner />
    )

  const renderHolidayTypeSelector = (a11yProps) =>
    props.categoryData && props.categoryData.length ? (
      <ListBox {...a11yProps} onChange={handleCategoryIdChange}>
        <ListBox.Trigger hasClearButton={false} />
        {props.categoryData
          .sort((a, b) => {
            return b.categoryID - a.categoryID
          })
          .map((item) => (
            <ListBox.Option isSelected={item.stayTypeID === stayTypeId} key={item.stayTypeID}>
              {isGemaParkBookingSystem && item.categoryName}
              {isAnytimeBookingSystem && item.name}
            </ListBox.Option>
          ))}
      </ListBox>
    ) : (
      <Spinner />
    )

  const renderHolidayTypeLabel = () => (
    <Label>
      {isGemaParkBookingSystem && "Holiday type"}
      {isAnytimeBookingSystem && "Category"}
    </Label>
  )

  const campsiteOccupancyDetails = campsiteOccupantTypeIds[props.campsiteinfo.name]

  const renderSelectOptions = (numOfOptions) => {
    return Array.from({ length: numOfOptions }, (val, ind) => ind).map((optionLabel) => (
      <option key={optionLabel} value={optionLabel}>
        {optionLabel}
      </option>
    ))
  }

  const renderAdultsSelect = () => (
    <Select
      defaultValue={String(numOfAdults)}
      placeholder="Select number of adults"
      onChange={(e) => {
        setNumOfAdults(e.target.value)
      }}
    >
      {renderSelectOptions(campsiteOccupancyDetails.adults.max)}
    </Select>
  )

  const renderChildrenSelect = () => (
    <Select
      defaultValue={String(numOfChildren)}
      placeholder="Select number of children"
      onChange={(e) => {
        setNumOfChildren(e.target.value)
      }}
    >
      {renderSelectOptions(campsiteOccupancyDetails.children.max)}
    </Select>
  )

  const renderInfantSelect = () => (
    <Select
      defaultValue={String(numOfInfants)}
      placeholder="Select number of infants"
      onChange={(e) => {
        setNumOfInfants(e.target.value)
      }}
    >
      {renderSelectOptions(campsiteOccupancyDetails.infants.max)}
    </Select>
  )

  const renderOccupancyInputs = () => (
    <>
      <FormElement style={formElementStyle}>
        {isMobile ? (
          <>
            <Label>Adults</Label>
            <Content>{renderAdultsSelect()}</Content>
          </>
        ) : (
          <Layout>
            <LeftCol width={180}>
              <Label>Adults</Label>
            </LeftCol>
            <RightCol style={{ marginTop: 0 }}>
              <Content>{renderAdultsSelect()}</Content>
            </RightCol>
          </Layout>
        )}
      </FormElement>
      <FormElement style={formElementStyle}>
        {isMobile ? (
          <>
            <Label>Children</Label>
            <Content>{renderChildrenSelect()}</Content>
          </>
        ) : (
          <Layout>
            <LeftCol width={180}>
              <Label>Children</Label>
            </LeftCol>
            <RightCol style={{ marginTop: 0 }}>
              <Content>{renderChildrenSelect()}</Content>
            </RightCol>
          </Layout>
        )}
      </FormElement>
      <FormElement style={formElementStyle}>
        {isMobile ? (
          <>
            <Label>Infants (Under 3)</Label>
            <Content>{renderInfantSelect()}</Content>
          </>
        ) : (
          <Layout>
            <LeftCol width={180}>
              <Label>Infants (Under 3)</Label>
            </LeftCol>
            <RightCol style={{ marginTop: 0 }}>
              <Content>{renderInfantSelect()}</Content>
            </RightCol>
          </Layout>
        )}
      </FormElement>
    </>
  )

  const renderContent = () => (
    <>
      <Fieldset>
        {holidayTypeCategoryMapping[props.campsiteinfo.name]?.showCategorySelect !== false ? (
          <FormElement style={formElementStyle}>
            {isMobile ? (
              <>
                {renderHolidayTypeLabel()}
                <Content>{(a11yProps) => renderHolidayTypeSelector(a11yProps)}</Content>
              </>
            ) : (
              <Layout>
                <LeftCol width={180}>{renderHolidayTypeLabel()}</LeftCol>
                <RightCol style={{ marginTop: 0 }}>
                  <Content>{(a11yProps) => renderHolidayTypeSelector(a11yProps)}</Content>
                </RightCol>
              </Layout>
            )}
          </FormElement>
        ) : null}
        {isUnitSelectorShowing && holidayTypeCategoryMapping[props.campsiteinfo.name]?.showUnitSelect !== false ? (
          <FormElement style={formElementStyle}>
            {isMobile ? (
              <>
                <Label>Accomodation type</Label>
                <Content>{(a11yProps) => renderUnitTypeSelector(a11yProps)}</Content>
              </>
            ) : (
              <Layout>
                <LeftCol width={180}>
                  <Label>Accomodation type</Label>
                </LeftCol>
                <RightCol style={{ marginTop: 0 }}>
                  <Content>{(a11yProps) => renderUnitTypeSelector(a11yProps)}</Content>
                </RightCol>
              </Layout>
            )}
          </FormElement>
        ) : null}
        <FormElement
          style={formElementStyle}
          help="For best availability results select a Fri/Sat/Sun arrival and departure. "
        >
          {isMobile ? (
            <>
              <Label>Select dates</Label>
              <Content>{renderDateRangePicker()}</Content>
            </>
          ) : (
            <Layout>
              <LeftCol width={180}>
                <Label>Select dates</Label>
              </LeftCol>
              <RightCol style={{ marginTop: 0 }}>
                <Content>{renderDateRangePicker()}</Content>
              </RightCol>
            </Layout>
          )}
          {endDateError ? <Error>{endDateError}</Error> : null}
        </FormElement>
        {props.campsiteinfo.showOccupancyChoices ? renderOccupancyInputs() : null}
      </Fieldset>
      <Button
        className="search-button"
        isDisabled={!isApplyEnabled}
        kind={Button.types.kind.PRIMARY}
        onClick={() => {
          const occupanyData = props.campsiteinfo.showOccupancyChoices
            ? { numOfAdults, numOfChildren, numOfInfants }
            : {}
          props.onApply({ unitId, startDate, endDate, categoryId, stayTypeId, ...occupanyData })
        }}
      >
        Search
      </Button>
      {props.isLoading && <Spinner />}
      {props.noResults && (
        <p
          className="no-results-message"
          dangerouslySetInnerHTML={{
            __html: noResultsMessageByCampsite[props.campsiteinfo.name],
          }}
        />
      )}
    </>
  )

  return isMobile ? renderInDiv() : renderInModal()
}

export default angular
  .module("makeamapAngularApp.availabilitycheckmodal", [])
  .component(
    "availabilitycheckmodal",
    react2angular(AvailabilityCheckModal, [
      "categoryData",
      "campsiteinfo",
      "initData",
      "isLoading",
      "onApply",
      "onCloseModal",
      "unitTypes",
      "anytimeMinNightsAndPricingDataAllUnits",
      "noResults",
      "minMaxOccupantTypesForUnitsData",
    ])
  ).name
